import type { BlockResolver } from "./btc-explorer";
import {
  findBitcoinSite,
  parseProof,
  type TimestampNode,
} from "./ots-walker";
import type { CheckResult } from "./types";

function hexToBytes(hex: string): Uint8Array {
  const lower = hex.toLowerCase();
  if (!/^[0-9a-f]*$/.test(lower) || lower.length % 2 !== 0) {
    throw new Error("ots: hex string invalid");
  }
  const out = new Uint8Array(lower.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = Number.parseInt(lower.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

// Verify a receipt's Bitcoin OTS attestation.
//
// OFFLINE checks (always run):
//   - OTS proof base64 + DER parse cleanly
//   - operations apply against receipt.merkle.root without parse error
//   - proof contains a BitcoinBlockHeaderAttestation
//   - declared block matches the attestation's block height
//
// ONLINE check (runs only when `resolveBlock` is provided):
//   - the proof's terminal value (running msg AT the BTC attestation site)
//     equals block N's actual merkle_root, fetched via the resolver
//   This is what closes the trust loop: anyone can re-verify that block N
//   is on the chain with a specific merkle_root, and the OTS proof shows
//   our digest reduces to that exact merkle_root. Together they prove the
//   stamp existed at-or-before the block's mined timestamp.
export async function verifyOTSAttestation(
  otsProofBase64: string,
  rootHex: string,
  declaredBlock: number,
  options?: { resolveBlock?: BlockResolver },
): Promise<CheckResult> {
  let proofBytes: Uint8Array;
  try {
    proofBytes = base64ToBytes(otsProofBase64);
  } catch {
    return { ok: false, reason: "OTS proof is not valid base64" };
  }

  let rootBytes: Uint8Array;
  try {
    rootBytes = hexToBytes(rootHex);
  } catch (err) {
    return {
      ok: false,
      reason: `merkle.root is not valid hex: ${(err as Error).message}`,
    };
  }

  let tree: TimestampNode;
  try {
    tree = await parseProof(proofBytes, rootBytes);
  } catch (err) {
    return {
      ok: false,
      reason: `OTS proof parse failed: ${(err as Error).message}`,
    };
  }

  const site = findBitcoinSite(tree);
  if (site === null) {
    return {
      ok: false,
      reason: "OTS proof contains no BitcoinBlockHeaderAttestation",
    };
  }

  if (site.height !== declaredBlock) {
    return {
      ok: false,
      reason: `block height mismatch: proof says ${site.height}, receipt says ${declaredBlock}`,
    };
  }

  // Online chain confirmation, if a resolver was provided.
  if (options?.resolveBlock) {
    let chainMerkleRoot: string;
    try {
      const block = await options.resolveBlock(site.height);
      chainMerkleRoot = block.merkleRootHex.toLowerCase();
    } catch (err) {
      return {
        ok: false,
        reason: `block ${site.height} lookup failed: ${(err as Error).message}`,
      };
    }
    const proofTerminalHex = bytesToHex(site.terminalMsg);
    if (proofTerminalHex !== chainMerkleRoot) {
      return {
        ok: false,
        reason: `terminal value mismatch: OTS proof commits to ${proofTerminalHex}, block ${site.height} merkle_root is ${chainMerkleRoot}`,
      };
    }
    return {
      ok: true,
      note: `OTS proof confirmed against Bitcoin block ${site.height} (merkle_root match)`,
    };
  }

  return {
    ok: true,
    note: `OTS proof commits to Bitcoin block ${site.height}; online chain confirmation not performed`,
  };
}
